// @flow
import React, { type Node, Fragment, lazy } from 'react';
import { connect } from 'react-redux';
import CarouselSliders from 'app/common/react/components/common/Carousel/CarouselSliders';
import cx from 'classnames';
import NavigateLink from 'app/common/react/ui/NavigateLink';
import PinRepClickThroughAffordanceIcon from 'app/common/react/components/growth/activation/PinRepClickThroughAffordanceIcon/PinRepClickThroughAffordanceIcon';
import PropTypes from 'prop-types';
import SafeSuspense from 'app/packages/safe-suspense';
import Testable from 'app/common/react/ui/Testable';
import type { BoardActivityItemType } from 'app/common/redux/reducers/activityItems';
import type { EditButtonStyleProp } from 'app/common/react/components/withPinEditButtonBehavior';
import type { Viewer } from 'app/packages/pinterest-flow-types/viewer';
import VideoIndicatorIcon from 'app/common/react/components/video/VideoIndicatorIcon';
import zIndices from 'app/common/react/components/DeprecatedPinRep/styles/zIndices';
import { BOARD_ACTIVITY_OBJECT_TYPES } from 'app/common/redux/reducers/activityItems';
import { buildPinDeeplinks } from 'app/common/lib/deeplinkRoutes';
import { Experiment } from 'app/common/react/components/experiments';
import { SingleTreatmentExp } from 'app/common/lib/experiment';
import { startTiming } from 'app/common/lib/TraceLogger/logTraceEvent';
import { track } from 'app/common/lib/react/analytics';
import { type SaveButtonOptions } from 'app/common/react/components/PinRep/components/PinRepPrimaryContent';
import PinRepClickThroughAffordance, {
  type LinkPosition,
} from 'app/common/react/components/growth/activation/PinRepClickThroughAffordance/PinRepClickThroughAffordance';
import type { State as ReduxState, Thunk } from 'app/common/redux/types';
import {
  RequestContextPropType,
  type RequestContextType,
} from 'app/common/react/components/RequestContext';
import {
  SelectionState,
  type PinRepType,
} from 'app/common/react/components/DeprecatedPinRep/DeprecatedPinRep';
import {
  updateActivityItemReaction,
  type ValidReactionTypes,
} from 'app/common/redux/actions/activityItem/updateActivityItem';
import { bindActionCreators, type Dispatch } from 'redux';
import { Box, Mask, Text } from 'gestalt';
import { buildPinRepLink, isVideoPlaying } from 'app/common/lib/util/PinUtils';

const NuxOneClickSaveButton = lazy(() =>
  import(/* webpackChunkName: "NuxOneClickSaveButtonCommon" */ /* webpackPrefetch: true */ 'app/common/react/components/growth/activation/NuxOneClickSaveButton/NuxOneClickSaveButton'),
);
const PersistentOneClickSave = lazy(() =>
  import(/*webpackChunkName: "PersistentOneClickSaveCommon" */ 'app/common/react/components/FeedsAndFollowing/Homefeed/MoreIdeasTabsNav/PersistentOneClickSave'),
);
const PinBetterSave = lazy(() =>
  import(/* webpackChunkName: "PinBetterSaveCommon" */ /* webpackPrefetch: true */ 'app/common/react/components/common/PinBetterSave/PinBetterSave'),
);
const PinPromoteButton = lazy(() =>
  import(/* webpackChunkName: "PinPromoteButtonCommon" */ /* webpackPrefetch: true */ 'app/common/react/components/DeprecatedPinRep/PinPromoteButton'),
);
const PinRepEditButton = lazy(() =>
  import(/* webpackChunkName: "PinRepEditButtonCommon" */ /* webpackPrefetch: true */ 'app/common/react/components/PinRep/components/PinRepEditButton'),
);
const PinSaveButton = lazy(() =>
  import(/* webpackChunkName: "PinSaveButtonCommon" */ /* webpackPrefetch: true */ 'app/common/react/components/save/PinSaveButton/PinSaveButton'),
);
const PinRepSendShareFlyoutButton = lazy(() =>
  import(/* webpackChunkName: "PinRepSendShareFlyoutButtonCommon" */ /* webpackPrefetch: true */ 'app/common/react/components/DeprecatedPinRep/PinRepSendShareFlyoutButton'),
);
// Note(jackhsu): we do not prefetch this because it is less likely for this component to be necessary
const BoardActivityPinRepLikeButton = lazy(() =>
  import(/* webpackChunkName: "BoardActivityPinRepLikeButtonCommon" */ 'app/common/react/components/BoardActivities/BoardActivityPinRepLikeButton'),
);

const OneTapOverlayInteractable = (props: any) => {
  const { style = {} } = props;
  const styles = {
    ...style,
    zIndex: zIndices.pinActionWrapper,
  };
  return <Box {...props} dangerouslySetInlineStyle={{ __style: styles }} />;
};

type OwnProps = {|
  boardActivity: ?{ id: string },
  buyableProduct?: {
    id?: string,
  },
  children: Node,
  componentType: number,
  contextLogData: Object,
  denseViewSelected?: boolean,
  description: string,
  domain: string,
  dominantColor?: string,
  doneCount?: number,
  hideSaveButton?: boolean,
  imageHeight: number,
  imageSignature: any,
  imageWidth: number,
  images?: {
    '236x': {
      url: string,
    },
  },
  inNewUserOneTapExperiment: boolean,
  isEligibleForAnalyticsCloseup?: boolean,
  isEligibleForWebCloseup: ?boolean,
  isFlyoutOpen: boolean,
  onSave: () => void,
  onToggleFlyout: Function,
  pin: PinRepType,
  pinTitle?: string,
  pinsBoardEditableByViewer?: boolean,
  requestContext: RequestContextType,
  saveOptions: SaveButtonOptions,
  searchOptions?: {
    query: string,
    scope: string,
  },
  selection: 'NO_SELECTION' | 'SELECTED' | 'UNSELECTED',
  showBoardActivityLikeButton?: boolean,
  showButtons: boolean,
  showClickthroughButton?: boolean,
  showCondensedInfo?: boolean,
  showEditPinButton: boolean,
  showOnPinAnalyticsNUX?: boolean,
  showPromoteButton?: boolean,
  showReactions?: boolean,
  showRepinButton: boolean,
  showSendButton: boolean,
  slotIndex?: number,
  sourceUrl?: string,
  truncatePinHeight?: boolean,
  viewParameter?: $Values<PCONST.thrift.ViewParameterType>,
  viewType?: $Values<PCONST.thrift.ViewType>,
  viewer: Viewer,
|};

type StateProps = {|
  currentlyPlayingVideo?: boolean,
  reactionByMe: ValidReactionTypes | PCONST.thrift.ReactionType.NONE,
|};

type ReduxDispatchProps = {|
  updateReaction: (
    id: string,
    type: BoardActivityItemType,
    oldReactionType: $Values<PCONST.thrift.ReactionType>,
    newReactionType: $Values<PCONST.thrift.ReactionType>,
  ) => Thunk<void>,
|};

type Props = {|
  ...OwnProps,
  ...StateProps,
  ...ReduxDispatchProps,
|};

type State = {|
  isReactionBarShowing: boolean,
|};

/**
 * PinActionWrapper
 * Pin image with button and navigation links overlayed
 */
export class PinActionWrapper extends React.Component<Props, State> {
  static propTypes = {
    buyableProduct: PropTypes.shape({
      id: PropTypes.string,
    }),
    componentType: PropTypes.number.isRequired,
    contextLogData: PropTypes.shape({}),
    description: PropTypes.string.isRequired,
    domain: PropTypes.string,
    dominantColor: PropTypes.string,
    doneCount: PropTypes.number,
    imageHeight: PropTypes.number.isRequired,
    imageWidth: PropTypes.number.isRequired,
    inNewUserOneTapExperiment: PropTypes.bool,
    isEligibleForWebCloseup: PropTypes.bool,
    onSave: PropTypes.func,
    onToggleFlyout: PropTypes.func.isRequired,
    pin: PropTypes.shape({
      attribution: PropTypes.shape({
        author_name: PropTypes.string,
        provider_favicon_url: PropTypes.string,
      }),
      buyable_product: PropTypes.shape({}),
      description: PropTypes.string,
      id: PropTypes.string.isRequired,
      image_url: PropTypes.string, // for new Pins
      image_crop: PropTypes.shape({
        max_y: PropTypes.number,
        min_y: PropTypes.number,
      }),
      images: PropTypes.shape({
        '236x': PropTypes.shape({
          width: PropTypes.number,
          height: PropTypes.number,
          url: PropTypes.string,
        }).isRequired,
        '474x': PropTypes.shape({
          width: PropTypes.number,
          height: PropTypes.number,
          url: PropTypes.string,
        }),
        '736x': PropTypes.shape({
          width: PropTypes.number,
          height: PropTypes.number,
          url: PropTypes.string,
        }),
        orig: PropTypes.shape({
          width: PropTypes.number,
          height: PropTypes.number,
          url: PropTypes.string,
        }),
      }),
      is_promoted: PropTypes.bool,
      link: PropTypes.string,
      place_summary: PropTypes.shape({
        name: PropTypes.string,
      }),
      rich_summary: PropTypes.shape({
        display_name: PropTypes.string,
        favicon_link: PropTypes.string,
        site_name: PropTypes.string,
      }),
    }),
    pinsBoardEditableByViewer: PropTypes.bool.isRequired,
    pinTitle: PropTypes.string,
    requestContext: RequestContextPropType,
    saveOptions: PropTypes.shape({
      type: PropTypes.oneOf(['classic', 'inline', 'controlled', 'persistentOneClickSave'])
        .isRequired,
    }).isRequired,
    searchOptions: PropTypes.shape({
      query: PropTypes.string.isRequired,
      scope: PropTypes.string.isRequired,
    }),
    selection: PropTypes.oneOf(['NO_SELECTION', 'SELECTED', 'UNSELECTED']).isRequired,
    showOnPinAnalyticsNUX: PropTypes.bool,
    showButtons: PropTypes.bool.isRequired,
    showCondensedInfo: PropTypes.bool,
    showEditPinButton: PropTypes.bool.isRequired,
    showPromoteButton: PropTypes.bool,
    showRepinButton: PropTypes.bool.isRequired,
    showSendButton: PropTypes.bool.isRequired,
    slotIndex: PropTypes.number,
    sourceUrl: PropTypes.string,
    // provided by redux
    // TODO: This is not logical w.r.t. our state shape
    //       but will work for now
    truncatePinHeight: PropTypes.bool,
    viewer: PropTypes.shape({
      isAuth: PropTypes.bool,
      username: PropTypes.string,
      id: PropTypes.string,
    }).isRequired,
    viewParameter: PropTypes.number,
    viewType: PropTypes.number,
    isFlyoutOpen: PropTypes.bool.isRequired,
    showClickthroughButton: PropTypes.bool,
  };

  static defaultProps = {
    onToggleFlyout: () => {},
    pinsBoardEditableByViewer: false,
    showCondensedInfo: false,
    showOnPinAnalyticsNUX: false,
    showPromoteButton: false,
  };

  state = {
    isReactionBarShowing: false,
  };

  onToggleFlyout = () => {
    const { isFlyoutOpen, onToggleFlyout } = this.props;
    onToggleFlyout(!isFlyoutOpen);
  };

  getViewInfo() {
    const { viewType, showEditPinButton } = this.props;
    const isBoardView = viewType === PCONST.thrift.ViewType.BOARD;
    const isSectionView = viewType === PCONST.thrift.ViewType.BOARD_SECTION;
    const isSearchView = viewType === PCONST.thrift.ViewType.SEARCH;
    const isProfileView = viewType === PCONST.thrift.ViewType.USER;
    const isOwnProfileView = isProfileView && showEditPinButton;
    const isOwnBoardView = (isBoardView || isSectionView) && showEditPinButton;
    const isOwnPinSearchView = isSearchView && showEditPinButton;

    return {
      isBoardView,
      isSearchView,
      isProfileView,
      isOwnProfileView,
      isOwnBoardView,
      isOwnPinSearchView,
    };
  }

  cropImageStyles() {
    const { imageHeight, pin, truncatePinHeight } = this.props;
    let croppedImageStyles = {};

    // Don't crop if story pin because story pins are forced into a 9:16 aspect ratio
    const isStoryPin = pin.story_pin_data_id;

    if (pin.image_crop && truncatePinHeight && !isStoryPin) {
      if (pin.image_crop.min_y !== 0 || pin.image_crop.max_y !== 1) {
        const min_y = pin.image_crop.min_y;
        const max_y = pin.image_crop.max_y;
        const bottomPx = min_y > 0 ? min_y * -1 * imageHeight + 'px' : '0px';
        const topPx = max_y < 1 ? Math.round((1 - max_y) * -1 * imageHeight) + 'px' : '0px';
        croppedImageStyles = {
          marginTop: bottomPx,
          marginBottom: topPx,
        };
      }
    }
    return croppedImageStyles;
  }

  handleOnSourceImageClick = () => {
    const { imageSignature, requestContext, slotIndex } = this.props;
    startTiming(PCONST.thrift.TracingClientTypes.PWTAction.PIN_CLOSEUP_DETAILS);

    // Activate into the Pin Stats from business profile on click
    // of image part of PinRep.
    new SingleTreatmentExp(requestContext, 'smb_pin_stats_from_business_profile').enabled();

    this.handleClickLogging(PCONST.thrift.ElementType.PIN_SOURCE_IMAGE, {
      grid_index: slotIndex,
      image_signature: imageSignature,
    });
  };

  handleClickLogging(elementType: string, extraData?: {}) {
    const {
      componentType,
      contextLogData,
      viewParameter,
      viewType,
      pin: { id: pinId },
    } = this.props;
    track(PCONST.thrift.EventType.CLICK, {
      ...contextLogData,
      ...extraData,
      component: componentType,
      view: viewType,
      viewParameter,
      element: elementType,
      objectId: pinId,
    });
  }

  handleToggleHover = (keepHover: boolean) => {
    this.props.onToggleFlyout(false, keepHover);
  };
  handleToggleSubmenu = (flyoutShown: boolean, keepHover: boolean) => {
    this.props.onToggleFlyout(flyoutShown, keepHover, 'save');
  };
  handleDropdownClose = () => {
    this.props.onToggleFlyout(false);
  };
  handleClickthroughLogging = () => {
    const { componentType, contextLogData, pin, slotIndex, viewType, viewParameter } = this.props;
    const { id: pinId } = pin;

    // Logs clickthrough event
    track(PCONST.thrift.EventType.PIN_CLICKTHROUGH, {
      ...contextLogData,
      view: viewType,
      viewParameter,
      component: componentType,
      objectId: pinId,
      grid_index: slotIndex,
    });

    // Logs click event
    // TODO(luna): remove this once we move to brio
    this.handleClickLogging(PCONST.thrift.ElementType.WEBSITE_BUTTON);
  };

  handleSaveButtonClick = () => {
    this.handleClickLogging(PCONST.thrift.ElementType.PIN_REPIN_BUTTON);
  };

  handleLikeButtonClick = ({ event }: { event: SyntheticMouseEvent<> }) => {
    if (event && event.stopPropagation) {
      event.stopPropagation();
      this.toggleReaction();
    }
  };

  handleLikeButtonTouch = (
    event: SyntheticMouseEvent<HTMLDivElement> | SyntheticKeyboardEvent<HTMLDivElement>,
  ) => {
    if (event && event.stopPropagation) {
      event.stopPropagation();
      this.toggleReaction();
    }
  };

  toggleReaction = () => {
    const { boardActivity, updateReaction, reactionByMe, pin } = this.props;
    const activityId = (boardActivity && boardActivity.id) || pin.id;

    // Either LIKE, or remove the previous reaction.
    let newReactionType = PCONST.thrift.ReactionType.NONE;
    if (reactionByMe === PCONST.thrift.ReactionType.NONE) {
      newReactionType = PCONST.thrift.ReactionType.LIKE;
    }

    updateReaction(
      activityId,
      BOARD_ACTIVITY_OBJECT_TYPES.BOARD_ACTIVITY,
      reactionByMe,
      newReactionType,
    );
  };

  renderSendShareButton(iconSize: string) {
    const {
      images,
      pin: { id: pinId },
      viewType,
    } = this.props;

    return (
      <Testable id="share-button">
        <SafeSuspense>
          <PinRepSendShareFlyoutButton
            imageSrc={images && images['236x'].url}
            onClick={() => this.handleClickLogging(PCONST.thrift.ElementType.PIN_SEND_BUTTON)}
            onToggleFlyout={this.onToggleFlyout}
            onFlyoutDismiss={this.handleDropdownClose}
            pinId={pinId}
            sendType="pin"
            shownWithinMasonry
            isFlyoutOpen={this.props.isFlyoutOpen}
            viewType={viewType}
            backgroundColor="transparentDarkGray"
            iconColor="white"
            iconSize={iconSize}
          />
        </SafeSuspense>
      </Testable>
    );
  }

  renderEditButton(editButtonStyles: EditButtonStyleProp) {
    const {
      pin: { id: pinId, carousel_data },
      viewParameter,
      viewType,
    } = this.props;

    return (
      <Testable id="edit-button">
        <SafeSuspense>
          <PinRepEditButton
            component={PCONST.thrift.ComponentType.FLOWED_PIN}
            pinId={pinId}
            onToggleModal={this.handleToggleHover}
            view={viewType}
            viewParameter={viewParameter}
            carouselSlotCurrentIndex={carousel_data && carousel_data.index}
            style={editButtonStyles}
          />
        </SafeSuspense>
      </Testable>
    );
  }

  renderEditAndSendRow(showSendButton: boolean, showEditPinButton: boolean) {
    const size = 'md';
    const iconProps = { bgColor: 'transparentDarkGray', iconColor: 'white', size };

    return (
      <OneTapOverlayInteractable padding={1} position="absolute" top left display="flex">
        {showSendButton && <Box padding={1}>{this.renderSendShareButton(size)}</Box>}
        {showEditPinButton && <Box padding={1}>{this.renderEditButton(iconProps)}</Box>}
      </OneTapOverlayInteractable>
    );
  }

  renderPrimaryButtons() {
    const {
      hideSaveButton,
      inNewUserOneTapExperiment,
      onSave,
      pin,
      showRepinButton,
      showPromoteButton,
      showCondensedInfo,
      showButtons: hovered,
      viewer,
      saveOptions,
      viewParameter,
      showClickthroughButton,
    } = this.props;

    const isControlledSave = saveOptions.type === 'controlled';
    const isPersistentOneClickSave = saveOptions && saveOptions.type === 'persistentOneClickSave';
    const saved = isControlledSave && saveOptions.saved && saveOptions.save;
    const showRepinOrPromote = showRepinButton || showPromoteButton;
    const renderPinSaveTooltip =
      saveOptions && saveOptions.type !== 'none' ? saveOptions.renderPinSaveTooltip : undefined;
    const shouldRender =
      saved ||
      (hovered && showRepinOrPromote) ||
      !!renderPinSaveTooltip ||
      isPersistentOneClickSave;

    if (!shouldRender) {
      if (showClickthroughButton) {
        return (
          <Box
            position="absolute"
            bottom
            color="transparentDarkGray"
            right
            marginBottom={2}
            marginRight={2}
            shape="pill"
          >
            <Box paddingX={3} paddingY={1}>
              <Text color="white" size="xs" weight="bold">
                Open
              </Text>
            </Box>
          </Box>
        );
      }

      return null;
    }

    if (hideSaveButton) {
      return null;
    }

    let primaryButton = null;

    const pinSaveButton = (
      <Testable id="pin-save-button">
        <SafeSuspense>
          <PinSaveButton
            onClickCallback={() => {
              this.handleClickLogging(PCONST.thrift.ElementType.PIN_REPIN_BUTTON);
            }}
            onToggleModal={this.handleToggleHover}
            oneTapSave={isControlledSave ? saveOptions : undefined}
            pin={pin}
            renderPinSaveTooltip={renderPinSaveTooltip}
          />
        </SafeSuspense>
      </Testable>
    );

    if (showPromoteButton) {
      primaryButton = (
        <Testable id="promote-button">
          <SafeSuspense>
            <PinPromoteButton pin={pin} />
          </SafeSuspense>
        </Testable>
      );
    } else if (saveOptions.type === 'inline') {
      primaryButton = (
        <Testable id="better-save">
          <SafeSuspense>
            <PinBetterSave
              initialSelection={saveOptions && saveOptions.initialSelection}
              useMasonryFlyout
              onSave={() => {
                if (saveOptions && saveOptions.onSave) {
                  saveOptions.onSave();
                }

                if (onSave) {
                  onSave();
                }
              }}
              onToggleFlyout={this.handleToggleSubmenu}
              onToggleModal={this.handleToggleHover}
              pin={pin}
              renderPinSaveTooltip={renderPinSaveTooltip}
              viewParameter={viewParameter}
              renderSavedStatus={null}
            />
          </SafeSuspense>
        </Testable>
      );
      // cannot use isPersistentOneClickSave here bc flow can't infer the types in SaveOption properly
    } else if (saveOptions.type === 'persistentOneClickSave') {
      primaryButton = (
        <SafeSuspense>
          <PersistentOneClickSave
            onToggleModal={this.handleToggleHover}
            pin={pin}
            saveOptions={saveOptions}
          />
        </SafeSuspense>
      );
    } else if (saved || (showRepinButton && viewer.isAuth)) {
      primaryButton = inNewUserOneTapExperiment ? (
        <Testable id="nux-one-click-save">
          <SafeSuspense>
            <NuxOneClickSaveButton onClickCallback={this.handleSaveButtonClick} pin={pin} />
          </SafeSuspense>
        </Testable>
      ) : (
        pinSaveButton
      );
    }

    if (showCondensedInfo) {
      primaryButton = pinSaveButton;
    }

    const width = saveOptions.type !== 'inline' || showCondensedInfo ? 'auto' : '100%';

    return (
      <OneTapOverlayInteractable padding={2} position="absolute" top right width={width}>
        {primaryButton}
      </OneTapOverlayInteractable>
    );
  }

  renderGradientOverlay(children: Node) {
    const { showButtons: hovered } = this.props;
    return (
      <Box position="relative">
        {children}
        {hovered && (
          <Box
            bottom
            left
            height="100%"
            position="absolute"
            right
            top
            width="100%"
            dangerouslySetInlineStyle={{
              __style: {
                backgroundColor: '#000',
                opacity: 0.3,
              },
            }}
          />
        )}
      </Box>
    );
  }

  renderClickThrough(position: LinkPosition) {
    const {
      denseViewSelected,
      domain,
      isEligibleForWebCloseup,
      pin: { id: pinId },
      sourceUrl,
    } = this.props;

    return (
      <PinRepClickThroughAffordance
        denseViewSelected={denseViewSelected}
        domain={domain}
        isEligibleForWebCloseup={isEligibleForWebCloseup}
        sourceUrl={sourceUrl}
        pinId={pinId}
        position={position}
        textColor="white"
        backgroundColor="transparentDarkGray"
        borderRadius={20}
        onClick={this.handleClickthroughLogging}
        hoverStyle={{
          backgroundColor: '#333',
        }}
        maxHeight={32}
        paddingX={0}
      />
    );
  }

  renderHolisticHoverOwnPinViewCondensed() {
    const {
      pin: { id: pinId },
      domain,
      sourceUrl,
      showEditPinButton,
    } = this.props;

    const size = 'md';
    const iconColor = 'white';
    const bgColor = 'transparentDarkGray';
    const iconProps = { size, iconColor, bgColor };
    const clickThroughProps = {
      pinId,
      domain,
      sourceUrl,
      bgColor: 'transparentDarkGray',
      ...iconProps,
    };

    return (
      <OneTapOverlayInteractable
        padding={1}
        width="100%"
        position="absolute"
        bottom
        left
        display="flex"
        alignItems="center"
        justifyContent="around"
      >
        <Box padding={1}>
          {/* clickThrough could return null, so set dimensions to ensure empty placeholder space */}
          <Box width={40} height={40}>
            <PinRepClickThroughAffordanceIcon
              {...clickThroughProps}
              onClick={this.handleClickthroughLogging}
            />
          </Box>
        </Box>
        <Box padding={1}>{showEditPinButton && this.renderEditButton(iconProps)}</Box>
        <Box padding={1}>{this.renderSendShareButton(size)}</Box>
      </OneTapOverlayInteractable>
    );
  }

  renderHolisticHoverOwnPinView() {
    const {
      showSendButton,
      showEditPinButton,
      pin: { id: pinId },
      domain,
      isEligibleForWebCloseup,
      sourceUrl,
    } = this.props;

    const size = 'sm';
    const iconProps = { iconColor: 'white', bgColor: 'transparentDarkGray', size };
    const clickThroughWidth = 65;
    const iconBoxWidth = 100 - clickThroughWidth;

    return (
      <OneTapOverlayInteractable
        padding={1}
        position="absolute"
        bottom
        left
        display="flex"
        width="100%"
        alignItems="center"
        height={50}
      >
        {/* clickThrough could return null, so set width to ensure empty placeholder space */}
        <Box width={`${clickThroughWidth}%`}>
          <PinRepClickThroughAffordance
            domain={domain}
            isEligibleForWebCloseup={isEligibleForWebCloseup}
            sourceUrl={sourceUrl}
            pinId={pinId}
            position="relative"
            textColor="white"
            backgroundColor="transparentDarkGray"
            borderRadius={20}
            onClick={this.handleClickthroughLogging}
            hoverStyle={{
              backgroundColor: '#333',
            }}
            maxHeight={32}
            paddingX={0}
          />
        </Box>
        <Box width={`${iconBoxWidth}%`} display="flex" justifyContent="end">
          {showEditPinButton && <Box padding={1}>{this.renderEditButton(iconProps)}</Box>}
          {showSendButton && <Box padding={1}>{this.renderSendShareButton(size)}</Box>}
        </Box>
      </OneTapOverlayInteractable>
    );
  }

  renderReactionButton(pin: PinRepType, reactionType: number) {
    const { denseViewSelected } = this.props;

    return (
      <Testable id="board-reactions-button">
        <SafeSuspense>
          <Experiment name="group_boards_reactions" activate={false}>
            {({
              anyEnabled: inReactionsExperiment,
              customActivate: activateReactionsExperiment,
            }) => (
              <BoardActivityPinRepLikeButton
                pin={pin}
                activateReactionsExperiment={activateReactionsExperiment}
                inReactionsExperiment={inReactionsExperiment}
                onShowReactionsBar={() => {
                  this.setState({ isReactionBarShowing: true });
                }}
                onHideReactionsBar={() => {
                  this.setState({ isReactionBarShowing: false });
                }}
                supportsReactionBar={!denseViewSelected}
              />
            )}
          </Experiment>
        </SafeSuspense>
      </Testable>
    );
  }

  renderSecondaryButtons() {
    const { showCondensedInfo } = this.props;

    if (showCondensedInfo) {
      return this.renderHolisticHoverOwnPinViewCondensed();
    }

    return this.renderHolisticHoverOwnPinView();
  }

  renderTopButtons() {
    const { pin, showReactions, showButtons: hovered, reactionByMe } = this.props;
    const { isReactionBarShowing } = this.state;

    let topButtons = null;

    if (showReactions) {
      const isSomeReaction = reactionByMe !== PCONST.thrift.ReactionType.NONE;
      if (hovered) {
        const width = isReactionBarShowing ? '100%' : null;
        topButtons = (
          <Fragment>
            <OneTapOverlayInteractable width={width} position="absolute" top left padding={2}>
              {this.renderReactionButton(pin, reactionByMe)}
            </OneTapOverlayInteractable>
            {this.renderSecondaryButtons()}
          </Fragment>
        );
      } else if (isSomeReaction) {
        topButtons = (
          <OneTapOverlayInteractable position="absolute" top left padding={2}>
            {this.renderReactionButton(pin, reactionByMe)}
          </OneTapOverlayInteractable>
        );
      }
    } else {
      topButtons = (
        <Fragment>
          {hovered && this.renderSecondaryButtons()}
          {this.renderPrimaryButtons()}
        </Fragment>
      );
    }

    return topButtons;
  }

  render() {
    const {
      currentlyPlayingVideo,
      children,
      isEligibleForAnalyticsCloseup,
      pin: { carousel_data, id: pinId, videos },
      selection,
      showButtons: hovered,
      viewParameter,
      viewType,
      componentType,
    } = this.props;

    const cropImageStyles = this.cropImageStyles();

    // Video Pins will always have an video_list exist but for the sake of making
    // this component generic we add a safety check. Additionally, using Object.keys()
    // is safe here as all properties under the video_list field will have the same
    // video duration, height, width, the only change between them is the url where
    // the specific video format is hosted. Ordering does not matter here and optional
    // chaining with defaults guarantees type safety.
    const isVideo = !!videos && !!videos.video_list;
    const { duration = 0 } =
      (videos && videos.video_list && videos.video_list[Object.keys(videos.video_list)[0]]) || {};

    return (
      <Box data-test-id={carousel_data ? 'carousel-pin' : ''} position="relative">
        {hovered &&
          carousel_data && (
            <CarouselSliders
              pinId={pinId}
              componentType={componentType}
              viewParameter={viewParameter}
              viewType={viewType}
            />
          )}
        <NavigateLink
          deeplinkUrls={buildPinDeeplinks(pinId)}
          onClick={this.handleOnSourceImageClick}
          forceRefresh={PCONST.ForceRefreshType.PAGE}
          extraData={{
            isReact: true,
            reactData: this.props.pin,
            searchOptions: this.props.searchOptions,
          }}
          style={{
            cursor: 'zoom-in',
            display: 'block',
            position: 'relative',
          }}
          to={buildPinRepLink(pinId, isEligibleForAnalyticsCloseup || false)}
        >
          <Mask shape="rounded" wash>
            <Box dangerouslySetInlineStyle={{ __style: cropImageStyles }} minHeight={55}>
              {this.renderGradientOverlay(children)}
            </Box>
          </Mask>

          {/* (@chrislloyd) be really freakin' careful removing this Box. There
              is a weird perf. regression that happens when you quickly hover
              over a bunch of pins if you remove it. I have no idea why.
           */}
          <Box position="absolute" top left bottom right />
        </NavigateLink>
        {selection !== SelectionState.NO_SELECTION && (
          <div
            className={cx('pinSelectionBorder', {
              selected: selection === SelectionState.SELECTED,
            })}
          />
        )}
        <Fragment>
          {this.renderTopButtons()}
          {/* We want to show the video camera icon atop the PinRep (no hover) to indicate
            the Pin is a video. But not when its autoplaying */}
          {!hovered && isVideo && !currentlyPlayingVideo ? (
            <OneTapOverlayInteractable padding={2} position="absolute" bottom left display="flex">
              <VideoIndicatorIcon duration={duration} />
            </OneTapOverlayInteractable>
          ) : null}
        </Fragment>
      </Box>
    );
  }
}

const mapStateToProps = (state: ReduxState, ownProps: OwnProps) => {
  const {
    pin: { id: pinId },
    boardActivity,
  } = ownProps;
  const videosAutoplaying = state.ui.app
    ? state.ui.app.videosAutoplaying
    : {
        organicVideosAutoplaying: {},
        promotedVideosAutoplaying: {},
      };
  const isCurrentlyPlaying = isVideoPlaying(pinId, videosAutoplaying);
  const activityId = (boardActivity && boardActivity.id) || pinId;
  const activityItem = state.activityItems[activityId];

  let reactionByMe = PCONST.thrift.ReactionType.NONE;
  if (
    activityItem &&
    activityItem.reaction_by_me !== null &&
    activityItem.reaction_by_me !== undefined
  ) {
    reactionByMe = activityItem.reaction_by_me;
  }

  return {
    currentlyPlayingVideo: isCurrentlyPlaying,
    reactionByMe,
  };
};

const mapDispatchToProps = (dispatch: Dispatch<any>, ownProps: OwnProps) => ({
  updateReaction: bindActionCreators(updateActivityItemReaction, dispatch),
});

// $FlowFixMe: new errors found from flow 0.96 upgrade
export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(PinActionWrapper);
